/** @jsxImportSource @emotion/react */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { AccodianWrap } from 'components/layouts/ContentLayout';
import GatingJudgeResultReqPopUp from 'pages/gtng/popup/GatingJudgeResultReqPopUp';
import GatingChangeRequest, {
  GatingReqType,
} from 'pages/gtng/gatingcheckresult/GatingChangeRequest';
import { convertJson } from 'utils/FormatUtil';
import ParameterManagementReqModal from 'components/modals/ip/ParameterManagementReqModal';
import MpEquipmentClassificationRequest from 'pages/mp/MpEquipmentClassificationRequest';
import UtMatrixReviewPage from '../ut/UtMatrixReviewPage';
import UtMatrixReviewRequest from '../ut/UtMatrixReviewRequest';
import IdleAssetRequest from '../asst/management/IdleAssetRequest';
import UtMatrixLibraryRequestPage from '../ut/library/UtMatrixLibraryRequestPage';
import SetupScheduleRequest from '../setup/schedule/SetupScheduleRequest';
import ExcpAuthRequest from 'pages/partner/excp-role/ExcpAuthRequest';
import AuthRequest from '../admin/rolemanagement/AuthRequest';

export enum ApproveRequestPageType {
  GATING_JUDGE_RESULT_REQ = 'GATING_JUDGE_RESULT_REQ',
  GATING_CANCEL = 'GATING_CANCEL',
  GATING_STATUS_CHANGE = 'GATING_STATUS_CHANGE',
  GATING_DATE_CHANGE = 'GATING_DATE_CHANGE',
  IP_APPROVE_REQ = 'IP_APPROVE_REQ',
  IP_UPDATE_REQ = 'IP_UPDATE_REQ',
  IP_DELETE_REQ = 'IP_DELETE_REQ',
  MP_CLSF_REQ = 'MP_CLSF_REQ',
  MP_CLSF_UPDATE_REQ = 'MP_CLSF_UPDATE_REQ',
  MP_CLSF_DELETE_REQ = 'MP_CLSF_DELETE_REQ',
  UT_REVIEW_REQ = 'UT_REVIEW_REQ',
  UT_LIB_REQ = 'UT_LIB_REQ',
  UT_LIB_UPDATE_REQ = 'UT_LIB_UPDATE_REQ',
  UT_LIB_DELETE_REQ = 'UT_LIB_DELETE_REQ',
  IDLE_ASST_REQ = 'IDLE_ASST_REQ',
  SETUP_SCHD_REG = 'SETUP_SCHD_REG', // 셋업일정등록
  SETUP_SCHD_CHG = 'SETUP_SCHD_CHG', // 셋업일정변경
  EXCP_AUTH_REQ = 'EXCP_AUTH_REQ',
  ELM_AUTH_REQ = 'ELM_AUTH_REQ',
}

type Props = {
  pageId: string;
  condition?: any;
  isReadOnly?: boolean;
  aprReqId: string;
  onChangePrj?: (val: any[]) => void;
  onChangeRole?: (val: any[]) => void;
};

const ApproveRequestDetailCase = (props: Props, ref) => {
  const { pageId, isReadOnly = false, aprReqId } = props;
  const { t } = useTranslation();
  const childRef = useRef<any>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [condition, setCondition] = useState(convertJson(props.condition));

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (!childRef.current?.validate) {
        return true;
      }
      return childRef.current?.validate?.();
    },
    getBizReqData: (aprReqId?: string, reqRsn?: string) => {
      return childRef.current?.getBizReqData?.(aprReqId, reqRsn);
    },
  }));

  useEffect(() => setCondition(convertJson(props.condition)), [props.condition]);
  return (
    condition && (
      <AccodianWrap className="marginTop">
        <Accordion expanded={isExpanded} onChange={() => setIsExpanded((prev) => !prev)}>
          <AccordionSummary>{t('com.label.결재 내용', '결재 내용')}</AccordionSummary>
          <AccordionDetails>
            {
              {
                GATING_JUDGE_RESULT_REQ: (
                  <GatingJudgeResultReqPopUp
                    ref={childRef}
                    condition={{
                      gatingId: condition?.gatingId,
                    }}
                  />
                ),
                GATING_CANCEL: (
                  <GatingChangeRequest
                    ref={childRef}
                    reqTp={GatingReqType.GATING_CANCEL}
                    gatingId={condition?.gatingId}
                    aprReqId={aprReqId}
                    isReadOnly={isReadOnly}
                  />
                ),
                GATING_STATUS_CHANGE: (
                  <GatingChangeRequest
                    ref={childRef}
                    reqTp={GatingReqType.STATUS_CHANGE}
                    gatingId={condition?.gatingId}
                    equipmentId={condition?.equipmentId}
                    aprReqId={aprReqId}
                    isReadOnly={isReadOnly}
                  />
                ),
                GATING_DATE_CHANGE: (
                  <GatingChangeRequest
                    ref={childRef}
                    reqTp={GatingReqType.DATE_CHANGE}
                    gatingId={condition?.gatingId}
                    equipmentId={condition?.equipmentId}
                    aprReqId={aprReqId}
                    isReadOnly={isReadOnly}
                  />
                ),
                IP_APPROVE_REQ: (
                  <ParameterManagementReqModal
                    ref={childRef}
                    aprReqId={aprReqId}
                    condition={condition}
                  />
                ),
                IP_UPDATE_REQ: (
                  <ParameterManagementReqModal
                    ref={childRef}
                    aprReqId={aprReqId}
                    condition={condition}
                  />
                ),
                IP_DELETE_REQ: (
                  <ParameterManagementReqModal
                    ref={childRef}
                    aprReqId={aprReqId}
                    condition={condition}
                  />
                ),
                MP_CLSF_REQ: (
                  <MpEquipmentClassificationRequest
                    ref={childRef}
                    aprReqId={aprReqId}
                    mode={condition?.mode}
                    requestMasterList={condition?.requestMasterList}
                    isReadOnly={isReadOnly}
                  />
                ),
                MP_CLSF_UPDATE_REQ: (
                  <MpEquipmentClassificationRequest
                    ref={childRef}
                    aprReqId={aprReqId}
                    mode={condition?.mode}
                    requestMasterList={condition?.requestMasterList}
                    isReadOnly={isReadOnly}
                  />
                ),
                MP_CLSF_DELETE_REQ: (
                  <MpEquipmentClassificationRequest
                    ref={childRef}
                    aprReqId={aprReqId}
                    mode={condition?.mode}
                    requestMasterList={condition?.requestMasterList}
                    isReadOnly={isReadOnly}
                  />
                ),
                UT_LIB_REQ: (
                  <UtMatrixLibraryRequestPage
                    ref={childRef}
                    aprReqId={aprReqId}
                    mode={condition?.mode}
                    requestMasterList={condition?.requestMasterList}
                    isReadOnly={isReadOnly}
                  />
                ),
                UT_LIB_UPDATE_REQ: (
                  <UtMatrixLibraryRequestPage
                    ref={childRef}
                    aprReqId={aprReqId}
                    mode={condition?.mode}
                    requestMasterList={condition?.requestMasterList}
                    isReadOnly={isReadOnly}
                  />
                ),
                UT_LIB_DELETE_REQ: (
                  <UtMatrixLibraryRequestPage
                    ref={childRef}
                    aprReqId={aprReqId}
                    mode={condition?.mode}
                    requestMasterList={condition?.requestMasterList}
                    isReadOnly={isReadOnly}
                  />
                ),
                UT_REVIEW_REQ: (
                  <UtMatrixReviewRequest
                    ref={childRef}
                    aprReqId={aprReqId}
                    mode={condition?.mode}
                    utmId={condition?.utmId}
                    isReadOnly={isReadOnly}
                  />
                ),
                IDLE_ASST_REQ: (
                  <IdleAssetRequest
                    ref={childRef}
                    aprReqId={aprReqId}
                    rvwReqId={condition?.rvwReqId}
                    rvmAsstTpCd={condition?.rvmAsstTpCd}
                    isReadOnly={isReadOnly}
                  />
                ),
                ELM_AUTH_REQ: (
                  <AuthRequest
                    ref={childRef}
                    aprReqId={aprReqId}
                    requestRoleList={condition?.requestRoleList}
                    isReadOnly={isReadOnly}
                  />
                ),
                SETUP_SCHD_REG: (
                  <SetupScheduleRequest
                    ref={childRef}
                    pageId={pageId}
                    aprReqId={aprReqId}
                    elmPrjId={condition?.elmPrjId}
                    excpId={condition?.excpId}
                    requestList={condition?.requestList}
                    isReadOnly={isReadOnly}
                  />
                ),
                SETUP_SCHD_CHG: (
                  <SetupScheduleRequest
                    ref={childRef}
                    pageId={pageId}
                    aprReqId={aprReqId}
                    elmPrjId={condition?.elmPrjId}
                    excpId={condition?.excpId}
                    requestList={condition?.requestList}
                    isReadOnly={isReadOnly}
                  />
                ),
                EXCP_AUTH_REQ: (
                  <ExcpAuthRequest
                    ref={childRef}
                    aprReqId={aprReqId}
                    isReadOnly={isReadOnly}
                    mode={condition?.mode}
                    onChangePrj={props.onChangePrj}
                    onChangeRole={props.onChangeRole}
                  />
                ),
              }[pageId]
            }
          </AccordionDetails>
        </Accordion>
      </AccodianWrap>
    )
  );
};

export default forwardRef(ApproveRequestDetailCase);
